import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Gavel, Play, Send, CheckCircle2, FileText, Sparkles,
  Users, ShieldCheck, AlertCircle, Plus,
} from "lucide-react";
import AppShell, { getActiveSchemeId } from "@/components/AppShell";
import { useAuth } from "@/auth/AuthContext";
import ConfirmationGate, { useConfirmationGate } from "@/components/ConfirmationGate";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const MOTION_OUTCOME_STYLE = {
  passed: { label: "Passed", bg: "var(--success-light)", color: "var(--success)" },
  failed: { label: "Failed", bg: "var(--alert-light)", color: "var(--alert)" },
  withdrawn: { label: "Withdrawn", bg: "var(--slate-light)", color: "var(--slate)" },
};

const MOTION_STATUS_STYLE = {
  pending: { label: "Pending", bg: "var(--slate-light)", color: "var(--slate)" },
  open: { label: "Voting open", bg: "var(--blue-light)", color: "var(--blue-dark)" },
  closed: { label: "Closed", bg: "var(--success-light)", color: "var(--success)" },
  withdrawn: { label: "Withdrawn", bg: "var(--slate-light)", color: "var(--slate)" },
};

export default function MeetingDetail() {
  const { meeting_id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const schemeId = getActiveSchemeId();
  const [meeting, setMeeting] = useState(null);
  const [motions, setMotions] = useState([]);
  const [rsvp, setRsvp] = useState(null);
  const [minutes, setMinutes] = useState(null);
  const [me, setMe] = useState(null);
  const [members, setMembers] = useState([]);
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noticeError, setNoticeError] = useState(null);

  // Sprint 2H Phase A — MEETING_NOTICE gate. Declared before the early-return
  // so hook order is stable regardless of loading state. (MINUTES_PUBLISH gate
  // is declared inside <MinutesPanel/> since publish fires from that child.)
  const noticeGate = useConfirmationGate({ onConfirmed: () => reload() });

  const reload = async () => {
    const [mtg, mts, rs, mb, lt] = await Promise.all([
      api.get(`/schemes/${schemeId}/meetings/${meeting_id}`),
      api.get(`/schemes/${schemeId}/meetings/${meeting_id}/motions`),
      api.get(`/schemes/${schemeId}/meetings/${meeting_id}/rsvp`),
      api.get(`/schemes/${schemeId}/members`),
      api.get(`/schemes/${schemeId}/lots`),
    ]);
    setMeeting(mtg.data);
    setMotions(mts.data);
    setRsvp(rs.data);
    setMembers(mb.data);
    setLots(lt.data);
    const own = mb.data.find((m) => m.user_id === user?.user_id);
    setMe(own || (user?.system_role === "platform_admin" ? { role: "chair", lot_ids: [] } : null));
    try {
      const min = await api.get(`/schemes/${schemeId}/meetings/${meeting_id}/minutes`);
      setMinutes(min.data);
    } catch (e) {
      if (e?.response?.status !== 404 && e?.response?.status !== 403) {
        console.warn("minutes load:", e?.message);
      }
      setMinutes(null);
    }
  };

  useEffect(() => {
    if (!schemeId) { navigate("/onboarding"); return; }
    (async () => {
      try { await reload(); } catch (err) { toast.error(apiError(err)); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, [meeting_id]);

  if (loading || !meeting) {
    return <AppShell><div className="max-w-5xl mx-auto px-5 py-10 text-sm" style={{ color: "var(--slate)" }}>Loading…</div></AppShell>;
  }

  const isChair = me?.role === "chair" || user?.system_role === "platform_admin";
  const isCommittee = me?.role === "committee" || isChair;

  // Lifecycle actions
  const sendNotice = async () => {
    setNoticeError(null);
    try {
      const r = await noticeGate.fire(
        "post",
        `/schemes/${schemeId}/meetings/${meeting_id}/notice`,
        {},
        { __scheme_id: schemeId },
      );
      if (r) { toast.success("Notice sent"); reload(); }
    } catch (e) {
      // Sprint 1 Week 1 — Item 1.V — render 400 notice_period_violation inline.
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 400 && detail && typeof detail === "object" && detail.error === "notice_period_violation") {
        setNoticeError(detail);
      } else {
        toast.error(apiError(e));
      }
    }
  };
  const startMeeting = async () => {
    try { await api.post(`/schemes/${schemeId}/meetings/${meeting_id}/start`); toast.success("Meeting started"); reload(); } catch (e) { toast.error(apiError(e)); }
  };
  const completeMeeting = async () => {
    if (!window.confirm("Complete this meeting? This is final.")) return;
    try {
      const { data } = await api.post(`/schemes/${schemeId}/meetings/${meeting_id}/complete`);
      toast.success(data.linked_compliance_item_id
        ? "Meeting completed — linked AGM compliance item auto-completed"
        : "Meeting completed");
      reload();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-5 lg:px-8 py-6 lg:py-8" data-testid="meeting-detail-page">
        <Link to="/scheme/meetings" className="inline-flex items-center gap-1 text-xs font-medium mb-4 hover:underline" style={{ color: "var(--slate)" }} data-testid="back-to-meetings">
          <ArrowLeft size={12} /> All meetings
        </Link>

        {/* Sprint 1 Week 1 — Item 1.V — inline 400 notice_period_violation banner */}
        {noticeError && (
          <div
            className="rounded-xl border p-4 mb-5 flex items-start gap-3"
            style={{ borderColor: "var(--alert)", background: "var(--alert-light)" }}
            data-testid="notice-violation-banner"
          >
            <div className="font-bold text-xs uppercase tracking-wider mt-0.5" style={{ color: "var(--alert)" }}>
              Notice period violation
            </div>
            <div className="flex-1 text-sm" style={{ color: "var(--navy)" }}>
              <p className="font-semibold" data-testid="notice-violation-message">{noticeError.message}</p>
              <p className="text-xs mt-1" style={{ color: "var(--slate)" }}>
                Required: <strong data-testid="notice-violation-required">{noticeError.required_days} day{noticeError.required_days === 1 ? "" : "s"}</strong>
                {" · "}Scheduled in: <strong data-testid="notice-violation-actual">{noticeError.actual_days} day{noticeError.actual_days === 1 ? "" : "s"}</strong>
                {" · "}{noticeError.state}{noticeError.regulation_module ? ` (${noticeError.regulation_module})` : ""}
              </p>
              <p className="text-[11px] italic mt-1" style={{ color: "var(--tagline-grey)" }}>
                {noticeError.legislation_reference}
              </p>
              <button
                onClick={() => setNoticeError(null)}
                className="text-[11px] font-semibold mt-2 underline"
                style={{ color: "var(--alert)" }}
                data-testid="notice-violation-dismiss"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="rounded-2xl border bg-white p-5 lg:p-6 mb-5" style={{ borderColor: "var(--border)" }}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded" style={{ background: "var(--blue-light)", color: "var(--blue-dark)" }} data-testid="meeting-type-badge">
                  {meeting.meeting_type}
                </span>
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded" style={{ background: "var(--slate-light)", color: "var(--slate)" }} data-testid="meeting-status-badge">
                  {meeting.status.replace("_", " ")}
                </span>
                {meeting.linked_compliance_item_id && (
                  <Link to={`/scheme/compliance/${meeting.linked_compliance_item_id}`} className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded inline-flex items-center gap-1 hover:underline" style={{ background: "var(--success-light)", color: "var(--success)" }} data-testid="compliance-link-badge">
                    <ShieldCheck size={10} /> Compliance auto-completed
                  </Link>
                )}
              </div>
              <h1 className="text-2xl font-bold leading-tight" style={{ color: "var(--navy)" }}>{meeting.title}</h1>
              <p className="text-sm mt-1" style={{ color: "var(--slate)" }}>
                {new Date(meeting.scheduled_at).toLocaleString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric", hour: "numeric", minute: "2-digit" })} · {meeting.location}
              </p>
            </div>
            {isChair && (
              <div className="flex items-center gap-2 flex-wrap">
                {meeting.status === "draft" && (
                  <Button size="sm" onClick={sendNotice} style={{ background: "var(--blue)", color: "white" }} data-testid="send-notice-btn">
                    <Send size={12} className="mr-1.5" /> Send notice
                  </Button>
                )}
                {(meeting.status === "draft" || meeting.status === "notice_sent") && (
                  <Button size="sm" variant="outline" onClick={startMeeting} data-testid="start-meeting-btn">
                    <Play size={12} className="mr-1.5" /> Start meeting
                  </Button>
                )}
                {(meeting.status === "in_progress" || meeting.status === "notice_sent") && (
                  <Button size="sm" variant="outline" onClick={completeMeeting} data-testid="complete-meeting-btn" style={{ borderColor: "var(--success)", color: "var(--success)" }}>
                    <CheckCircle2 size={12} className="mr-1.5" /> Complete
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Notice */}
        <Section title="Notice" icon={<Send size={14} />} testid="notice-section">
          {meeting.notice_text ? (
            <div className="prose prose-sm max-w-none whitespace-pre-wrap text-sm" style={{ color: "var(--navy)" }} data-testid="notice-text">
              {meeting.notice_text}
            </div>
          ) : (
            <p className="text-xs" style={{ color: "var(--tagline-grey)" }}>No notice drafted yet.</p>
          )}
          {meeting.notice_sent_at && (
            <p className="text-[11px] mt-3 pt-3 border-t" style={{ borderColor: "var(--border)", color: "var(--slate)" }}>
              Notice sent {new Date(meeting.notice_sent_at).toLocaleString()}.
            </p>
          )}
        </Section>

        {/* RSVP */}
        <RSVPPanel scheme_id={schemeId} meeting_id={meeting_id} rsvp={rsvp} members={members} me={me} isCommittee={isCommittee} reload={reload} />

        {/* Motions */}
        <MotionsPanel
          scheme_id={schemeId} meeting={meeting} motions={motions} me={me} lots={lots} user={user}
          isChair={isChair} isCommittee={isCommittee} reload={reload}
        />

        {/* AI Minutes */}
        {isCommittee && ["in_progress", "completed"].includes(meeting.status) && (
          <MinutesPanel
            scheme_id={schemeId} meeting_id={meeting_id} meeting={meeting} minutes={minutes}
            members={members} isChair={isChair} reload={reload}
          />
        )}
        {!isCommittee && minutes?.status === "published" && (
          <Section title="Published minutes" icon={<FileText size={14} />} testid="published-minutes-readonly">
            <div className="prose prose-sm max-w-none whitespace-pre-wrap" data-testid="minutes-published-text">
              {minutes.final_minutes}
            </div>
            <p className="text-[11px] mt-4 pt-3 border-t italic" style={{ borderColor: "var(--border)", color: "var(--tagline-grey)" }}>
              {minutes.disclaimer}
            </p>
          </Section>
        )}
      </div>
      {/* Sprint 2H Phase A — MEETING_NOTICE confirmation gate. */}
      <ConfirmationGate {...noticeGate.gateProps} />
    </AppShell>
  );
}

function Section({ title, icon, children, testid, action }) {
  return (
    <div className="rounded-2xl border bg-white p-5 lg:p-6 mb-5" style={{ borderColor: "var(--border)" }} data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold uppercase tracking-wider flex items-center gap-2" style={{ color: "var(--navy)" }}>
          <span style={{ color: "var(--blue)" }}>{icon}</span> {title}
        </h3>
        {action}
      </div>
      {children}
    </div>
  );
}

function RSVPPanel({ scheme_id, meeting_id, rsvp, members, me, isCommittee, reload }) {
  const own = rsvp?.own_rsvp;
  const counts = rsvp?.counts || { attending: 0, apologies: 0, proxy: 0, no_response: 0 };
  const [busy, setBusy] = useState(false);
  const setMyRsvp = async (status, proxy_holder_user_id) => {
    setBusy(true);
    try {
      await api.post(`/schemes/${scheme_id}/meetings/${meeting_id}/rsvp`, {
        status, proxy_holder_user_id,
      });
      toast.success(`RSVP recorded as ${status}`);
      reload();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Section title="RSVP" icon={<Users size={14} />} testid="rsvp-section">
      <div className="flex flex-wrap gap-2 mb-4">
        <RsvpCount label="Attending" n={counts.attending} color="var(--success)" testid="rsvp-count-attending" />
        <RsvpCount label="Apologies" n={counts.apologies} color="var(--warning)" testid="rsvp-count-apologies" />
        <RsvpCount label="Proxy" n={counts.proxy} color="var(--blue-dark)" testid="rsvp-count-proxy" />
      </div>
      {me && (
        <div className="flex flex-wrap items-center gap-2 pt-3 border-t" style={{ borderColor: "var(--border)" }}>
          <span className="text-xs font-medium" style={{ color: "var(--slate)" }}>Your RSVP:</span>
          {["attending", "apologies"].map((s) => (
            <Button
              key={s}
              size="sm"
              variant={own?.status === s ? "default" : "outline"}
              style={own?.status === s ? { background: "var(--blue)", color: "white" } : {}}
              disabled={busy}
              onClick={() => setMyRsvp(s)}
              data-testid={`rsvp-btn-${s}`}
            >
              {s}
            </Button>
          ))}
          <ProxyPicker members={members} me={me} own={own} onPick={(uid) => setMyRsvp("proxy", uid)} disabled={busy} />
        </div>
      )}
      {isCommittee && rsvp?.all && rsvp.all.length > 0 && (
        <div className="mt-4 pt-3 border-t" style={{ borderColor: "var(--border)" }} data-testid="rsvp-list">
          <div className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: "var(--slate)" }}>All members</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
            {rsvp.all.map((r) => (
              <div key={r.rsvp_id} className="text-xs flex items-center justify-between py-1" style={{ color: "var(--navy)" }}>
                <span>{r.user_name || r.user_email}</span>
                <span className="font-mono text-[10px]" style={{ color: "var(--slate)" }}>{r.status}{r.proxy_holder_name ? ` → ${r.proxy_holder_name}` : ""}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Section>
  );
}

function RsvpCount({ label, n, color, testid }) {
  return (
    <div className="rounded-lg border px-3 py-2 text-center" style={{ borderColor: "var(--border)", minWidth: 80 }} data-testid={testid}>
      <div className="text-xl font-bold tabular-nums" style={{ color }}>{n}</div>
      <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: "var(--tagline-grey)" }}>{label}</div>
    </div>
  );
}

function ProxyPicker({ members, me, own, onPick, disabled }) {
  const others = members.filter((m) => m.user_id !== me?.user_id);
  const isSelected = own?.status === "proxy";
  return (
    <Select value={isSelected ? own?.proxy_holder_user_id : ""} onValueChange={onPick} disabled={disabled}>
      <SelectTrigger
        data-testid="rsvp-proxy-select"
        className="h-9 w-44"
        style={isSelected ? { borderColor: "var(--blue)", color: "var(--blue-dark)" } : {}}
      >
        <SelectValue placeholder="Or assign proxy" />
      </SelectTrigger>
      <SelectContent>
        {others.map((m) => (
          <SelectItem key={m.user_id} value={m.user_id}>{m.name || m.email}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function MotionsPanel({ scheme_id, meeting, motions, me, lots, user, isChair, isCommittee, reload }) {
  const meeting_id = meeting.meeting_id;
  const [open, setOpen] = useState(false);
  const myLots = useMemo(() => {
    const ownLotIds = me?.lot_ids || [];
    return lots.filter((l) => ownLotIds.includes(l.lot_id));
  }, [lots, me]);

  const addMotion = async (form) => {
    try { await api.post(`/schemes/${scheme_id}/meetings/${meeting_id}/motions`, form); toast.success("Motion added"); setOpen(false); reload(); } catch (e) { toast.error(apiError(e)); }
  };
  const motionAction = async (motion_id, action) => {
    try { await api.post(`/schemes/${scheme_id}/meetings/${meeting_id}/motions/${motion_id}/${action}`); toast.success(`Motion ${action}d`); reload(); } catch (e) { toast.error(apiError(e)); }
  };
  const voteOn = async (motion_id, vote, lot_id) => {
    try { await api.post(`/schemes/${scheme_id}/meetings/${meeting_id}/motions/${motion_id}/vote`, { vote, lot_id }); toast.success(`Vote recorded: ${vote}`); reload(); } catch (e) { toast.error(apiError(e)); }
  };

  const action = isCommittee && meeting.status !== "completed" ? (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="add-motion-btn">
        <Plus size={12} className="mr-1.5" /> Add motion
      </Button>
      <DialogContent className="max-w-lg">
        <MotionCreateForm onSubmit={addMotion} onCancel={() => setOpen(false)} />
      </DialogContent>
    </Dialog>
  ) : null;

  return (
    <Section title="Motions" icon={<Gavel size={14} />} testid="motions-section" action={action}>
      {motions.length === 0 ? (
        <p className="text-xs py-4 text-center" style={{ color: "var(--tagline-grey)" }}>No motions tabled yet.</p>
      ) : (
        <div className="space-y-3" data-testid="motions-list">
          {motions.map((mt) => (
            <MotionCard
              key={mt.motion_id} m={mt} meeting={meeting} isChair={isChair} isCommittee={isCommittee}
              myLots={myLots} me={me}
              onAction={motionAction} onVote={voteOn}
            />
          ))}
        </div>
      )}
    </Section>
  );
}

function MotionCard({ m, meeting, isChair, isCommittee, myLots, me, onAction, onVote }) {
  const ss = MOTION_STATUS_STYLE[m.status];
  const oo = m.outcome ? MOTION_OUTCOME_STYLE[m.outcome] : null;
  const tally = m.tally_for + m.tally_against + m.tally_abstain;
  const requiredPct = m.motion_type === "ordinary" ? 50 : m.motion_type === "special" ? 75 : 100;
  // Sprint 1 Week 1 — Item 2.V — AGM/EGM uses lot_entitlement weighting; Committee uses 1-per-vote.
  const usesEntitlement = meeting.meeting_type === "AGM" || meeting.meeting_type === "EGM";
  const entFor = m.tally_for_entitlement ?? m.tally_for;
  const entAgainst = m.tally_against_entitlement ?? m.tally_against;
  const entAbstain = m.tally_abstain_entitlement ?? m.tally_abstain;
  return (
    <div className="rounded-xl border p-4" style={{ borderColor: "var(--border)" }} data-testid={`motion-card-${m.motion_id}`}>
      <div className="flex items-start gap-2 mb-2">
        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded" style={{ background: "var(--slate-light)", color: "var(--slate)" }}>
          M{m.sequence} · {m.motion_type}
        </span>
        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded" style={{ background: ss.bg, color: ss.color }} data-testid={`motion-status-${m.motion_id}`}>
          {ss.label}
        </span>
        {oo && (
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded" style={{ background: oo.bg, color: oo.color }} data-testid={`motion-outcome-${m.motion_id}`}>
            {oo.label}
          </span>
        )}
      </div>
      <h4 className="font-semibold text-sm" style={{ color: "var(--navy)" }}>{m.title}</h4>
      {m.description && <p className="text-xs mt-1" style={{ color: "var(--slate)" }}>{m.description}</p>}

      {/* Tally — Sprint 1 Week 1 — Item 2.V — entitlement display for AGM/EGM */}
      <div className="mt-3 flex items-center gap-3 flex-wrap text-xs" style={{ color: "var(--slate)" }} data-testid={`motion-tally-${m.motion_id}`}>
        <span className="font-mono">For <strong style={{ color: "var(--success)" }}>{m.tally_for}</strong></span>
        <span className="font-mono">Against <strong style={{ color: "var(--alert)" }}>{m.tally_against}</strong></span>
        <span className="font-mono">Abstain <strong>{m.tally_abstain}</strong></span>
        {tally > 0 && m.tally_for + m.tally_against > 0 && (
          <span className="font-mono text-[10px]" style={{ color: "var(--tagline-grey)" }}>
            ({Math.round((m.tally_for / (m.tally_for + m.tally_against)) * 100)}% yes · needs ≥{requiredPct}%)
          </span>
        )}
      </div>
      {usesEntitlement && (
        <div
          className="mt-2 flex items-center gap-3 flex-wrap text-xs rounded-md px-2 py-1.5"
          style={{ background: "var(--blue-light)", color: "var(--blue-dark)" }}
          data-testid={`motion-entitlement-tally-${m.motion_id}`}
        >
          <span className="font-bold text-[10px] uppercase tracking-wider">Entitlement</span>
          <span className="font-mono">For <strong style={{ color: "var(--success)" }} data-testid={`motion-entitlement-for-${m.motion_id}`}>{entFor}</strong></span>
          <span className="font-mono">Against <strong style={{ color: "var(--alert)" }} data-testid={`motion-entitlement-against-${m.motion_id}`}>{entAgainst}</strong></span>
          <span className="font-mono">Abstain <strong data-testid={`motion-entitlement-abstain-${m.motion_id}`}>{entAbstain}</strong></span>
          {entFor + entAgainst > 0 && (
            <span className="font-mono text-[10px] opacity-70">
              ({Math.round((entFor / (entFor + entAgainst)) * 100)}% yes by entitlement · needs ≥{requiredPct}%)
            </span>
          )}
        </div>
      )}
      {m.outcome && (
        <div
          className="mt-2 text-[10px] font-semibold uppercase tracking-wider"
          style={{ color: "var(--tagline-grey)" }}
          data-testid={`motion-count-method-${m.motion_id}`}
        >
          {usesEntitlement ? "Decided by entitlement" : "Decided by member count"}
        </div>
      )}

      {/* Chair actions */}
      {isChair && (
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          {m.status === "pending" && meeting.status === "in_progress" && (
            <Button size="sm" variant="outline" onClick={() => onAction(m.motion_id, "open")} data-testid={`motion-open-${m.motion_id}`}>Open voting</Button>
          )}
          {m.status === "open" && (
            <Button size="sm" variant="outline" onClick={() => onAction(m.motion_id, "close")} data-testid={`motion-close-${m.motion_id}`}>Close & compute</Button>
          )}
          {(m.status === "pending" || m.status === "open") && (
            <Button size="sm" variant="ghost" onClick={() => onAction(m.motion_id, "withdraw")} style={{ color: "var(--alert)" }} data-testid={`motion-withdraw-${m.motion_id}`}>Withdraw</Button>
          )}
        </div>
      )}

      {/* Voting */}
      {m.status === "open" && (
        <VoteControls m={m} meeting={meeting} myLots={myLots} me={me} onVote={onVote} />
      )}

      {/* Phase 6 — non-binding intent pre-poll */}
      {(m.status === "pending" || m.status === "open") && (meeting.status === "notice_sent" || meeting.status === "in_progress") && (
        <IntentPrePoll motion_id={m.motion_id} scheme_id={meeting.scheme_id} meeting_id={meeting.meeting_id} isChair={isChair} />
      )}
    </div>
  );
}

function VoteControls({ m, meeting, myLots, me, onVote }) {
  // AGM/EGM: per-lot voting if user owns lots; single own-right vote otherwise.
  const isAgm = meeting.meeting_type !== "Committee";
  const needsLot = isAgm && myLots.length > 0;
  const [lotId, setLotId] = useState(myLots[0]?.lot_id || "");

  // Committee meeting: only chair/committee can vote
  if (!isAgm && !["chair", "committee"].includes(me?.role)) {
    return <p className="text-[11px] mt-3 pt-3 border-t" style={{ borderColor: "var(--border)", color: "var(--tagline-grey)" }}>Only chair / committee can vote on committee meetings.</p>;
  }

  return (
    <div className="mt-3 pt-3 border-t flex flex-wrap items-center gap-2" style={{ borderColor: "var(--border)" }}>
      <span className="text-xs font-medium" style={{ color: "var(--slate)" }}>Cast your vote:</span>
      {needsLot && (
        <Select value={lotId} onValueChange={setLotId}>
          <SelectTrigger className="h-8 w-32" data-testid={`vote-lot-select-${m.motion_id}`}><SelectValue/></SelectTrigger>
          <SelectContent>
            {myLots.map((l) => <SelectItem key={l.lot_id} value={l.lot_id}>Lot {l.lot_number}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      {["for", "against", "abstain"].map((v) => (
        <Button
          key={v} size="sm" variant="outline"
          onClick={() => onVote(m.motion_id, v, needsLot ? lotId : null)}
          data-testid={`vote-${v}-${m.motion_id}`}
          style={{
            borderColor: v === "for" ? "var(--success)" : v === "against" ? "var(--alert)" : "var(--border)",
            color: v === "for" ? "var(--success)" : v === "against" ? "var(--alert)" : "var(--slate)",
          }}
        >
          {v}
        </Button>
      ))}
    </div>
  );
}

function IntentPrePoll({ motion_id, scheme_id, meeting_id, isChair }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/schemes/${scheme_id}/meetings/${meeting_id}/motions/${motion_id}/intent`);
      setData(data);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [motion_id]);

  const submit = async (intent) => {
    setBusy(true);
    try {
      await api.post(`/schemes/${scheme_id}/meetings/${meeting_id}/motions/${motion_id}/intent`, { intent });
      toast.success(`Intent recorded: ${intent} (non-binding)`);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const my = data?.my_intent;
  return (
    <div className="mt-3 pt-3 border-t" style={{ borderColor: "var(--border)" }} data-testid={`intent-prepoll-${motion_id}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "var(--blue-dark)" }}>Non-binding pre-poll</span>
        <span className="text-[10px] px-2 py-0.5 rounded" style={{ background: "var(--slate-light)", color: "var(--slate)" }}>Indicate your voting intent</span>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {["for", "against", "abstain"].map((v) => (
          <Button
            key={v}
            size="sm"
            variant={my === v ? "default" : "outline"}
            onClick={() => submit(v)}
            disabled={busy}
            data-testid={`intent-${v}-${motion_id}`}
            style={{
              borderColor: my === v ? undefined : (v === "for" ? "var(--success)" : v === "against" ? "var(--alert)" : "var(--border)"),
              background: my === v ? (v === "for" ? "var(--success)" : v === "against" ? "var(--alert)" : "var(--slate)") : undefined,
              color: my === v ? "#fff" : (v === "for" ? "var(--success)" : v === "against" ? "var(--alert)" : "var(--slate)"),
            }}
          >
            {v}
          </Button>
        ))}
      </div>
      {isChair && data?.counts && (
        <div className="mt-2 text-xs flex items-center gap-3 flex-wrap" style={{ color: "var(--slate)" }} data-testid={`intent-aggregate-${motion_id}`}>
          <span className="font-mono">Pre-poll · For <strong style={{ color: "var(--success)" }}>{data.counts.for}</strong></span>
          <span className="font-mono">Against <strong style={{ color: "var(--alert)" }}>{data.counts.against}</strong></span>
          <span className="font-mono">Abstain <strong>{data.counts.abstain}</strong></span>
          <span className="font-mono text-[10px]" style={{ color: "var(--tagline-grey)" }}>({data.total} indicated)</span>
        </div>
      )}
      <p className="mt-2 text-[10px] italic" style={{ color: "var(--tagline-grey)" }}>
        Pre-poll intent is non-binding and visible to the chair only as an aggregate. Your actual vote during the meeting is what counts.
      </p>
    </div>
  );
}

function MotionCreateForm({ onSubmit, onCancel }) {
  const [form, setForm] = useState({
    title: "",
    description: "",
    motion_type: "ordinary",
    category: "other",
    voting_method: "show_of_hands_recorded",
  });
  const set = (k) => (v) => setForm((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  return (
    <>
      <DialogHeader><DialogTitle>Add motion</DialogTitle></DialogHeader>
      <form onSubmit={(e) => { e.preventDefault(); onSubmit(form); }} className="space-y-3 mt-2" data-testid="motion-create-form">
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--slate)" }}>Title</Label>
          <Input className="mt-1" value={form.title} onChange={set("title")} required data-testid="motion-title-input"/>
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--slate)" }}>Description</Label>
          <Textarea className="mt-1" rows={3} value={form.description} onChange={set("description")} data-testid="motion-desc-input"/>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--slate)" }}>Type</Label>
            <Select value={form.motion_type} onValueChange={set("motion_type")}>
              <SelectTrigger className="mt-1" data-testid="motion-type-select"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="ordinary">Ordinary (simple majority)</SelectItem>
                <SelectItem value="special">Special (75% required)</SelectItem>
                <SelectItem value="unanimous">Unanimous (100% required)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--slate)" }}>Category</Label>
            <Select value={form.category} onValueChange={set("category")}>
              <SelectTrigger className="mt-1" data-testid="motion-cat-select"><SelectValue/></SelectTrigger>
              <SelectContent>
                {["financial","by_laws","maintenance","insurance","administrative","other"].map((c) =>
                  <SelectItem key={c} value={c}>{c.replace("_"," ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
          <Button type="submit" style={{ background: "var(--blue)", color: "white" }} data-testid="motion-create-submit">Add</Button>
        </DialogFooter>
      </form>
    </>
  );
}

function MinutesPanel({ scheme_id, meeting_id, meeting, minutes, members, isChair, reload }) {
  const [chair_notes, setChairNotes] = useState("");
  const [attendees, setAttendees] = useState("");
  const [apologies, setApologies] = useState("");
  const [editing, setEditing] = useState("");
  const [generating, setGenerating] = useState(false);
  const [showPublish, setShowPublish] = useState(false);

  useEffect(() => {
    if (minutes) {
      setEditing(minutes.final_minutes || "");
      setChairNotes(minutes.chair_notes_raw || "");
    }
  }, [minutes]);

  const [genStreaming, setGenStreaming] = useState(false);
  const [genLive, setGenLive] = useState("");

  // Sprint 2H Phase A — MINUTES_PUBLISH gate.
  const publishGate = useConfirmationGate({
    onConfirmed: () => { setShowPublish(false); reload(); },
  });

  const generate = async () => {
    if (!chair_notes.trim()) { toast.error("Add chair notes first"); return; }
    setGenerating(true); setGenStreaming(true); setGenLive("");
    const att = attendees.split(",").map((s) => s.trim()).filter(Boolean);
    const apo = apologies.split(",").map((s) => s.trim()).filter(Boolean);
    const { streamSSE } = await import("@/lib/sseStream");
    const token = localStorage.getItem("opstrai.session_token");
    await streamSSE({
      url: `${process.env.REACT_APP_BACKEND_URL}/api/schemes/${scheme_id}/meetings/${meeting_id}/minutes/generate`,
      body: { chair_notes, attendees: att.length ? att : undefined, apologies: apo.length ? apo : undefined },
      headers: { Authorization: `Bearer ${token}` },
      onEvent: ({ event, data }) => {
        if (event === "token") { setGenerating(false); setGenLive((p) => p + (data?.text || "")); }
        else if (event === "done") {
          toast.success("AI minutes draft generated");
          setEditing(data.draft_minutes || data.final_minutes || "");
          setGenStreaming(false); setGenerating(false); setGenLive("");
          reload();
        } else if (event === "error") {
          toast.error(data?.message || "AI provider error");
          setGenStreaming(false); setGenerating(false);
        }
      },
      onError: (err) => {
        toast.error(err?.message || apiError(err) || "Failed");
        setGenStreaming(false); setGenerating(false);
      },
    });
  };

  const saveEdit = async () => {
    try { await api.patch(`/schemes/${scheme_id}/meetings/${meeting_id}/minutes`, { final_minutes: editing }); toast.success("Saved draft"); reload(); } catch (e) { toast.error(apiError(e)); }
  };

  const publish = async () => {
    try {
      // save first if edited
      if (minutes && editing !== minutes.final_minutes) {
        await api.patch(`/schemes/${scheme_id}/meetings/${meeting_id}/minutes`, { final_minutes: editing });
      }
      const r = await publishGate.fire(
        "post",
        `/schemes/${scheme_id}/meetings/${meeting_id}/minutes/publish`,
        {},
        { __scheme_id: scheme_id },
      );
      if (r) {
        toast.success("Minutes published");
        setShowPublish(false);
        reload();
      }
    } catch (e) { toast.error(apiError(e)); }
  };

  const isPublished = minutes?.status === "published";

  return (
    <Section title="AI Minutes" icon={<Sparkles size={14} />} testid="minutes-section">
      {!minutes ? (
        <div className="space-y-3" data-testid="minutes-blank">
          <p className="text-xs" style={{ color: "var(--slate)" }}>
            Paste the chair's raw notes — Claude Sonnet 4.5 will draft a structured 8-section minutes record with the recorded motion outcomes injected.
          </p>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--slate)" }}>Chair notes</Label>
            <Textarea rows={6} className="mt-1 text-xs" value={chair_notes} onChange={(e) => setChairNotes(e.target.value)} placeholder="Chair noted that quorum was confirmed at 6:35pm…" data-testid="chair-notes-input"/>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--slate)" }}>Attendees (comma-separated)</Label>
              <Input className="mt-1" value={attendees} onChange={(e) => setAttendees(e.target.value)} data-testid="attendees-input"/>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--slate)" }}>Apologies (comma-separated)</Label>
              <Input className="mt-1" value={apologies} onChange={(e) => setApologies(e.target.value)} data-testid="apologies-input"/>
            </div>
          </div>
          {isChair && (
            <Button onClick={generate} disabled={generating} style={{ background: "var(--blue)", color: "white" }} data-testid="generate-minutes-btn">
              <Sparkles size={14} className="mr-1.5" /> {generating ? "Drafting with Claude…" : "Generate AI draft"}
            </Button>
          )}
          {genStreaming && genLive && (
            <div className="mt-4 rounded-md border p-3" style={{ borderColor: "var(--blue)" }} data-testid="minutes-streaming">
              <div className="flex items-center gap-2 mb-2 text-[11px] font-semibold uppercase tracking-wider" style={{ color: "var(--blue)" }}>
                <span className="ai-dot"/> Streaming…
              </div>
              <pre className="whitespace-pre-wrap text-xs font-sans" style={{ color: "var(--navy)" }} data-testid="minutes-live">{genLive}</pre>
            </div>
          )}
        </div>
      ) : (
        <div data-testid="minutes-existing">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded" style={{ background: isPublished ? "var(--success-light)" : "var(--warning-light)", color: isPublished ? "var(--success)" : "var(--warning)" }} data-testid="minutes-status-badge">
              {isPublished ? "Published" : "DRAFT — review before publishing"}
            </span>
            <span className="text-[10px]" style={{ color: "var(--tagline-grey)" }}>
              Generated by {minutes.ai_model} · {new Date(minutes.generated_at).toLocaleString()}
            </span>
          </div>
          {isPublished ? (
            <div className="prose prose-sm max-w-none whitespace-pre-wrap text-sm" style={{ color: "var(--navy)" }} data-testid="minutes-published-text">
              {minutes.final_minutes}
            </div>
          ) : (
            <Textarea
              rows={18}
              className="font-mono text-xs"
              value={editing}
              onChange={(e) => setEditing(e.target.value)}
              data-testid="minutes-editor"
              readOnly={!isChair}
            />
          )}
          {isChair && !isPublished && (
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <Button size="sm" variant="outline" onClick={saveEdit} data-testid="save-minutes-btn">Save draft</Button>
              <Button size="sm" variant="outline" onClick={generate} disabled={generating} data-testid="regenerate-minutes-btn">
                <Sparkles size={12} className="mr-1.5" />{generating ? "Regenerating…" : "Regenerate"}
              </Button>
              <Button size="sm" onClick={() => setShowPublish(true)} style={{ background: "var(--success)", color: "white" }} data-testid="publish-minutes-btn">
                <CheckCircle2 size={12} className="mr-1.5" /> Publish
              </Button>
            </div>
          )}
          <p className="text-[11px] mt-4 pt-3 border-t italic" style={{ borderColor: "var(--border)", color: "var(--tagline-grey)" }}>
            {minutes.disclaimer}
          </p>
        </div>
      )}

      <Dialog open={showPublish} onOpenChange={setShowPublish}>
        <DialogContent>
          <DialogHeader><DialogTitle>Publish minutes?</DialogTitle></DialogHeader>
          <p className="text-sm" style={{ color: "var(--slate)" }}>
            Publishing locks editing and makes the minutes visible to all lot owners on this scheme.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPublish(false)}>Cancel</Button>
            <Button onClick={publish} style={{ background: "var(--success)", color: "white" }} data-testid="confirm-publish-btn">Publish</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sprint 2H Phase A — MINUTES_PUBLISH confirmation gate. */}
      <ConfirmationGate {...publishGate.gateProps} />
    </Section>
  );
}
